import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Link2, TrendingUp, BarChart3, CheckCircle2 } from 'lucide-react'
import { config } from '@workspace/public-utils'
import { Seo } from '../../components/Seo'

interface ConsecutiveExample {
  round_number: number
  numbers: number[]
  consecutive_pairs: number[][]
}

interface ConsecutiveAnalysisData {
  round_range: number
  total_rounds: number
  consecutive_count: number
  consecutive_percentage: number
  consecutive_examples?: ConsecutiveExample[]
  calculated_at: string
}

const ConsecutiveNumbersPage = () => {
  const [selectedRange, setSelectedRange] = useState(0) // 0 = 10, 1 = 30, 2 = 50
  const [analysisData, setAnalysisData] = useState<Record<number, ConsecutiveAnalysisData>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ranges = [10, 30, 50]

  useEffect(() => {
    fetchAnalysis(ranges[selectedRange])
  }, [selectedRange])

  const fetchAnalysis = async (range: number) => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(
        `${config.API_BASE_URL}/api/v1/public/lottery/statistics/consecutive-numbers?range=${range}`
      )

      if (!response.ok) {
        throw new Error('분석 데이터를 불러오는데 실패했습니다.')
      }

      const data = await response.json()
      setAnalysisData((prev) => ({ ...prev, [range]: data }))
    } catch (err) {
      console.error('Error fetching consecutive numbers analysis:', err)
      setError(err instanceof Error ? err.message : '데이터를 불러올 수 없습니다.')
    } finally {
      setLoading(false)
    }
  }

  const currentData = analysisData[ranges[selectedRange]]

  const renderNumberBall = (num: number, isConsecutive: boolean = false) => {
    return (
      <div
        className={`inline-flex items-center justify-center w-12 h-12 rounded-full font-bold text-lg shadow-md transition-all duration-300 ${
          isConsecutive
            ? 'bg-gradient-to-br from-green-400 to-emerald-600 text-white ring-4 ring-green-200'
            : 'bg-gradient-to-br from-gray-200 to-gray-400 dark:from-gray-600 dark:to-gray-700 text-gray-800 dark:text-gray-100'
        }`}
      >
        {num}
      </div>
    )
  }

  const renderExampleCard = (example: ConsecutiveExample, index: number) => {
    const consecutiveSet = new Set(example.consecutive_pairs.flat())
    
    return (
      <motion.div
        key={`${example.round_number}-${index}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.1 }}
        className="bg-white dark:bg-gray-900 rounded-xl shadow-lg dark:shadow-none dark:border dark:border-gray-800 p-6 hover:shadow-2xl dark:hover:border-gray-700 transition-all duration-300"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <CheckCircle2 className="w-6 h-6 text-green-500 mr-2" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              제 {example.round_number}회
            </h3>
          </div>
          <div className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200 rounded-full text-sm font-bold">
            {example.consecutive_pairs.length}개 연속쌍
          </div>
        </div>

        {/* Numbers Display */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {example.numbers.map((num, idx) => (
            <div key={idx} className="flex items-center">
              {renderNumberBall(num, consecutiveSet.has(num))}
              {idx < example.numbers.length - 1 && (
                <span className="mx-1 text-gray-400 dark:text-gray-500">•</span>
              )}
            </div>
          ))}
        </div>

        {/* Consecutive Pairs */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 font-medium">연속 번호 쌍:</p>
          <div className="flex flex-wrap gap-2">
            {example.consecutive_pairs.map((pair, pairIdx) => (
              <div
                key={pairIdx}
                className="flex items-center gap-1 px-3 py-1 bg-green-50 dark:bg-green-950/50 rounded-lg"
              >
                <span className="text-green-700 dark:text-green-300 font-bold">{pair[0]}</span>
                <Link2 className="w-4 h-4 text-green-500" />
                <span className="text-green-700 dark:text-green-300 font-bold">{pair[1]}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      <Seo
        title="연속 번호 패턴 분석"
        description="역대 당첨번호에서 연속 번호(예: 11,12)가 포함되는 빈도와 예시를 통계로 확인해 보세요."
        canonicalPath="/analysis/consecutive-numbers"
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/analysis" className="inline-flex items-center text-white hover:text-white/80 mb-6 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            분석 방법으로 돌아가기
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center mb-4">
              <Link2 className="w-12 h-12 text-white mr-4" />
              <h1 className="text-4xl md:text-6xl font-black text-white">
                연속 번호 패턴
              </h1>
            </div>
            <p className="text-xl text-white/90 text-center max-w-3xl mx-auto">
              역대 당첨 번호 중 연속 번호(예: 11, 12)가 포함된 비율을 통계로 보여줍니다
            </p>
          </motion.div>
        </div>
      </section>

      {/* Range Selector */}
      <section className="py-8 px-4 bg-white shadow-sm dark:bg-gray-900 dark:shadow-none dark:border-b dark:border-gray-800 transition-colors">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">분석 기간 선택</h2>
            <div className="flex flex-wrap gap-2">
              {ranges.map((range, index) => (
                <button
                  key={range}
                  onClick={() => setSelectedRange(index)}
                  data-testid={`range-selector-${range}`}
                  className={`px-6 py-3 rounded-lg font-bold transition-all duration-300 ${
                    selectedRange === index
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg scale-105'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  최근 {range}회
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-green-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-red-50 dark:bg-red-950/40 border-l-4 border-red-500 p-6 rounded-lg">
            <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Analysis Content */}
      {!loading && !error && currentData && (
        <div className="max-w-7xl mx-auto px-4 py-12" data-testid="consecutive-numbers-section">
          {/* Info Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-blue-50 dark:bg-blue-950/40 border-l-4 border-blue-500 p-6 rounded-lg mb-12"
          >
            <p className="text-gray-700 dark:text-gray-300">
              <strong>분석 기준:</strong> 최근 {currentData.round_range}회차 (총 <span data-testid="total-rounds">{currentData.total_rounds}</span>회 분석됨)
              <br />
              <strong>업데이트:</strong> {new Date(currentData.calculated_at).toLocaleString('ko-KR')}
            </p>
          </motion.div>

          {/* Statistics Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid md:grid-cols-3 gap-6 mb-12"
          >
            {/* Percentage Card */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-xl p-8 text-white">
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="w-10 h-10 opacity-80" />
                <BarChart3 className="w-8 h-8 opacity-60" />
              </div>
              <div className="text-6xl font-black mb-2" data-testid="consecutive-percentage">
                {currentData.consecutive_percentage.toFixed(1)}%
              </div>
              <p className="text-white/90 text-lg">연속 번호 출현 비율</p>
            </div>

            {/* Consecutive Count Card */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl dark:shadow-none p-8 border-2 border-green-200 dark:border-green-800">
              <div className="flex items-center justify-center mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-500" />
              </div>
              <div className="text-6xl font-black text-gray-900 dark:text-white mb-2 text-center" data-testid="consecutive-count">
                {currentData.consecutive_count}회
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-lg text-center">연속 번호 포함 회차</p>
            </div>

            {/* Total Rounds Card */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl dark:shadow-none p-8 border-2 border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-center mb-4">
                <BarChart3 className="w-10 h-10 text-gray-500 dark:text-gray-400" />
              </div>
              <div className="text-6xl font-black text-gray-900 dark:text-white mb-2 text-center">
                {currentData.total_rounds}회
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-lg text-center">전체 분석 회차</p>
            </div>
          </motion.div>

          {/* Explanation Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/40 dark:to-emerald-950/40 rounded-2xl p-8 mb-12 border border-transparent dark:border-gray-800"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
              <Link2 className="w-7 h-7 text-green-600 mr-3" />
              연속 번호란?
            </h3>
            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
              연속 번호는 당첨 번호 중 연속된 두 개의 숫자를 의미합니다. 
              예를 들어, 11과 12, 또는 33과 34처럼 1 차이가 나는 숫자들입니다.
              <br /><br />
              <strong className="text-green-700 dark:text-green-400">최근 {currentData.round_range}회차 분석 결과:</strong> 
              총 {currentData.total_rounds}회 중 <strong className="text-green-700 dark:text-green-400">{currentData.consecutive_count}회 ({currentData.consecutive_percentage.toFixed(1)}%)</strong>의 
              당첨 번호에 연속 번호가 포함되어 있었습니다.
            </p>
          </motion.div>

          {/* Examples Section */}
          {currentData.consecutive_examples && currentData.consecutive_examples.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 flex items-center">
                <CheckCircle2 className="w-8 h-8 text-green-500 mr-3" />
                연속 번호 포함 사례
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="consecutive-examples">
                {currentData.consecutive_examples.map((example, index) => 
                  renderExampleCard(example, index)
                )}
              </div>
            </motion.div>
          )}

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12 bg-yellow-50 dark:bg-yellow-950/30 border-l-4 border-yellow-500 p-6 rounded-lg"
          >
            <p className="text-gray-700 dark:text-gray-300">
              <strong className="text-yellow-700 dark:text-yellow-400">⚠️ 안내:</strong> 이 분석은 과거 당첨 번호의 통계적 패턴 분석이며, 
              미래 당첨 번호를 예측하거나 보장하지 않습니다. 
              로또는 무작위 추첨이므로 모든 번호와 조합은 동일한 당첨 확률을 가집니다.
            </p>
          </motion.div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && !currentData && (
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <p className="text-xl text-gray-600 dark:text-gray-400">
            분석 데이터가 없습니다. 로또 결과 데이터가 충분히 쌓이면 분석이 표시됩니다.
          </p>
        </div>
      )}
    </div>
  )
}

export default ConsecutiveNumbersPage

